/**
 * BundleBuilderHandler
 *
 * Manages UI updates for Rebuy Bundle Builder products in the cart:
 * - Detects bundle items using their DOM attributes and discount messages
 * - Locks the Smart Cart discount disclosure when bundles are present
 * - Applies bundle discounts with proper price distribution across items
 * - Preserves original prices to prevent compounding discounts
 * - Handles cart updates, removals, and quantity changes
 *
 * Uses RebuyEventManager for event subscriptions and RebuyUtils for discount allocation.
 */

window.BundleBuilderHandler = (function () {
  // Private variables
  let debug = false; // Debug mode toggle
  let initialized = false; // Prevents multiple initializations
  let activeDiscountPercent = 0; // Currently applied discount percentage
  let activeBundleCount = 0; // Number of bundle items in cart
  let originalPrices = new Map(); // Stores original prices before discounts
  let initAttempts = 0; // Tracks initialization attempts
  let isBundleDiscountDisclosureLocked = false;
  const MAX_INIT_ATTEMPTS = 20; // Maximum attempts before giving up

  /**
   * Debug logging helper - only logs when debug mode is enabled
   */
  function log(...args) {
    if (debug) {
      console.log('[BundleBuilderHandler]', ...args);
    }
  }

  /**
   * Checks if all required Rebuy components are loaded and ready
   * @returns {boolean} True if Rebuy is ready for use
   */
  function isRebuyReady() {
    return !!(
      window.Rebuy &&
      window.Rebuy.Cart &&
      typeof window.Rebuy.Cart.getCart === 'function' &&
      window.RebuyEventManager &&
      window.RebuyUtils &&
      typeof window.RebuyUtils.allocateDiscountAcrossItems === 'function'
    );
  }

  /**
   * Detects bundle items in the cart using DOM-based approach
   * @returns {Array} Array of DOM elements that are bundle items
   */
  function detectBundleItems() {
    // Find all cart items
    const allCartItems = document.querySelectorAll('.rebuy-cart__flyout-item');

    // Filter to bundle items (two detection methods)
    const bundleItems = Array.from(allCartItems).filter(item => {
      // Method 1: Check for bundle builder attribution in class name
      const hasAttributionClass = item.className.includes(
        'property-_attribution:rebuy-bundle-builder'
      );

      // Method 2: Check for discount message that indicates a bundle
      const discountMessage = item.querySelector('.rebuy-cart__flyout-item-discount-message');
      const hasDiscountMessage =
        discountMessage?.textContent.includes('Rabatt angewendet') || false;

      return hasAttributionClass || hasDiscountMessage;
    });

    log(`Detected ${bundleItems.length} bundle items`);
    return bundleItems;
  }

  /**
   * Extracts discount percentage from bundle items or uses count-based logic as fallback
   * @param {Array} bundleItems - DOM elements of bundle items
   * @returns {number} Discount percentage to apply
   */
  function calculateBundleDiscount(bundleItems) {
    if (!bundleItems.length) return 0;

    // Try to extract discount from message (most accurate)
    for (const item of bundleItems) {
      const discountMessage = item.querySelector('.rebuy-cart__flyout-item-discount-message');
      if (discountMessage) {
        const match = discountMessage.textContent.match(/(\d+)%/);
        if (match) {
          const percent = parseInt(match[1], 10);
          log(`Extracted ${percent}% discount from message: "${discountMessage.textContent}"`);
          return percent;
        }
      }
    }

    // Fallback to count-based logic if message extraction fails
    const bundleCount = bundleItems.length;
    log(`Using count-based discount for ${bundleCount} items`);

    // Apply tiered discounts based on number of items
    if (bundleCount >= 6) return 20;
    if (bundleCount >= 4) return 15;
    if (bundleCount >= 3) return 10;
    if (bundleCount >= 2) return 5;

    return 0;
  }

  /**
   * Resets all prices to their original state (before discounting)
   */
  function resetBundlePrices() {
    originalPrices.forEach((originalPrice, itemId) => {
      const item = document.querySelector(`[data-item-id="${itemId}"]`);
      if (!item) return;

      const priceContainer = item.querySelector('.rebuy-cart__flyout-item-price');
      if (!priceContainer) return;

      // Restore original price display
      priceContainer.innerHTML = `
        <span class="rebuy-money">
          <span class="sr-only">Price</span>
          <span>€ ${originalPrice}</span>
        </span>
      `;
    });

    log('Reset prices for bundle items');
  }

  /**
   * Captures original prices from DOM elements
   * Only captures each price once to prevent referencing already-discounted prices
   * @param {Array} bundleItems - DOM elements of bundle items
   */
  function captureOriginalPrices(bundleItems) {
    bundleItems.forEach(item => {
      const itemId = item.getAttribute('data-item-id');
      // Only capture if we don't already have this price
      if (!originalPrices.has(itemId)) {
        // Strategy: Look for either compare-at price (if discount already applied)
        // or regular price (if no discount yet)
        const compareAtPrice = item.querySelector(
          '.rebuy-cart__flyout-item-price .rebuy-money.compare-at span:last-child'
        );
        const regularPrice = item.querySelector(
          '.rebuy-cart__flyout-item-price .rebuy-money span:last-child'
        );

        if (compareAtPrice) {
          // If there's a compare-at price, use that as the original
          const price = compareAtPrice.textContent.trim().replace('€', '').trim();
          originalPrices.set(itemId, price);
          log(`Stored original price from compare-at for item ${itemId}: ${price}`);
        } else if (regularPrice) {
          // Otherwise use the regular price
          const price = regularPrice.textContent.trim().replace('€', '').trim();
          originalPrices.set(itemId, price);
          log(`Stored original price from regular for item ${itemId}: ${price}`);
        }
      }
    });
  }

  /**
   * Converts DOM items to cart-item-like objects for discount allocation
   * @param {Array} bundleItems - DOM elements of bundle items
   * @returns {Array} Array of objects compatible with allocateDiscountAcrossItems
   */
  function prepareCartItems(bundleItems) {
    const cartItems = [];

    for (const item of bundleItems) {
      const itemId = item.getAttribute('data-item-id');

      // Always use the original price to prevent compounding discounts
      if (!originalPrices.has(itemId)) {
        log(`No original price stored for item ${itemId}, cannot prepare cart item`);
        continue;
      }

      const originalPrice = originalPrices.get(itemId);

      // Parse quantity from the DOM
      const quantityElement = item.querySelector('.rebuy-cart__flyout-item-quantity-widget-label');
      let quantity = 1;
      if (quantityElement) {
        const qtyText = quantityElement.textContent.trim();
        const qtyMatch = qtyText.match(/\d+/);
        if (qtyMatch) {
          quantity = parseInt(qtyMatch[0], 10);
        }
      }

      // Convert to cents for the allocator (Shopify standard)
      const priceEuro = parseFloat(originalPrice.replace(',', '.'));
      const priceCents = Math.round(priceEuro * 100);

      cartItems.push({
        id: itemId,
        price: priceCents.toString(), // Shopify stores price as string
        quantity: quantity,
      });
    }

    log(`Prepared ${cartItems.length} cart items for allocation`);
    return cartItems;
  }

  /**
   * Updates item prices with discounts using allocation for penny-perfect distribution
   * @param {Array} bundleItems - DOM elements of bundle items
   * @param {number} discountPercent - Discount percentage to apply
   */
  function updateItemPrices(bundleItems, discountPercent) {
    if (!bundleItems.length || discountPercent <= 0) return;

    log(`Updating prices for ${bundleItems.length} items with ${discountPercent}% discount`);

    // First capture original prices if we don't have them yet
    captureOriginalPrices(bundleItems);

    try {
      // Prepare cart-like items for allocation using ORIGINAL prices
      const cartItems = prepareCartItems(bundleItems);

      if (cartItems.length === 0) {
        log('No cart items could be prepared, cannot update prices');
        return;
      }

      // Use RebuyUtils to allocate discount properly with penny-perfect distribution
      const { allocations } = window.RebuyUtils.allocateDiscountAcrossItems(
        cartItems,
        discountPercent
      );

      // Update UI with allocated discounts
      bundleItems.forEach(item => {
        const itemId = item.getAttribute('data-item-id');
        const priceContainer = item.querySelector('.rebuy-cart__flyout-item-price');
        if (!priceContainer || !itemId || !allocations.has(itemId)) return;

        const allocation = allocations.get(itemId);
        const originalPrice = originalPrices.get(itemId);

        if (!originalPrice) {
          log(`Missing original price for item ${itemId}, skipping update`);
          return;
        }

        // Format with comma as decimal separator for European markets
        const discountedPrice = allocation.discountedPrice.toFixed(2).replace('.', ',');

        // Update the display
        priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Discounted Price</span>
              <span>€ ${discountedPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original Price</span>
              <span>€ ${originalPrice}</span>
            </span>
          </div>
        `;

        log(`Updated price for item ${itemId}: ${originalPrice} -> ${discountedPrice}`);
      });
    } catch (error) {
      console.error('[BundleBuilderHandler] Error in discount allocation:', error);

      // Fallback to simple percentage discount if allocation fails
      // Not as precise but better than no discount
      bundleItems.forEach(item => {
        const itemId = item.getAttribute('data-item-id');
        if (!originalPrices.has(itemId)) return;

        const priceContainer = item.querySelector('.rebuy-cart__flyout-item-price');
        if (!priceContainer) return;

        const originalPrice = originalPrices.get(itemId);

        // Convert to number, being careful about comma as decimal separator
        const price = parseFloat(originalPrice.replace(',', '.'));

        // Calculate discounted price with simple percentage
        const discountedPrice = (price * (1 - discountPercent / 100)).toFixed(2).replace('.', ',');

        // Update the display
        priceContainer.innerHTML = `
          <div>
            <span class="rebuy-money sale">
              <span class="sr-only">Discounted Price</span>
              <span>€ ${discountedPrice}</span>
            </span>
            <span class="rebuy-money compare-at">
              <span class="sr-only">Original Price</span>
              <span>€ ${originalPrice}</span>
            </span>
          </div>
        `;

        log(`Fallback: Updated price for item ${itemId}: ${originalPrice} -> ${discountedPrice}`);
      });
    }
  }

  function storeDisclosureAttribute(disclosure, summary, attribute, datasetPrefix) {
    const presentKey = `${datasetPrefix}Present`;
    const valueKey = `${datasetPrefix}Value`;
    if (presentKey in disclosure.dataset) return;

    disclosure.dataset[presentKey] = String(summary.hasAttribute(attribute));
    disclosure.dataset[valueKey] = summary.getAttribute(attribute) || '';
  }

  function restoreDisclosureAttribute(disclosure, summary, attribute, datasetPrefix) {
    const presentKey = `${datasetPrefix}Present`;
    const valueKey = `${datasetPrefix}Value`;
    if (!(presentKey in disclosure.dataset)) return;

    if (disclosure.dataset[presentKey] === 'true') {
      summary.setAttribute(attribute, disclosure.dataset[valueKey]);
    } else {
      summary.removeAttribute(attribute);
    }

    delete disclosure.dataset[presentKey];
    delete disclosure.dataset[valueKey];
  }

  function suspendURLDiscountDisclosureObserver(discountInputWrapper) {
    const observer = discountInputWrapper._urlDiscountDisclosureObserver;
    if (!observer) return;

    // The URL and bundle managers both lock this disclosure. Keep the bundle
    // message authoritative while a bundle is in the cart, then let the URL
    // manager restore its own state after the bundle lock is released.
    observer.disconnect();
    delete discountInputWrapper._urlDiscountDisclosureObserver;
    log('Suspended URL discount disclosure observer while bundle lock is active');
  }

  function attachBundleDisclosureObserver(discountInputWrapper) {
    if (discountInputWrapper._bundleDiscountDisclosureObserver) return;

    const observer = new MutationObserver(() => {
      if (isBundleDiscountDisclosureLocked) {
        setBundleDiscountDisclosureState(true);
      }
    });

    observer.observe(discountInputWrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'open'],
    });
    discountInputWrapper._bundleDiscountDisclosureObserver = observer;
    log('Bundle discount disclosure observer attached');
  }

  function detachBundleDisclosureObserver(discountInputWrapper) {
    const observer = discountInputWrapper._bundleDiscountDisclosureObserver;
    if (!observer) return;

    observer.disconnect();
    delete discountInputWrapper._bundleDiscountDisclosureObserver;
    log('Bundle discount disclosure observer removed');
  }

  /**
   * Locks the Smart Cart discount disclosure while bundle products are present.
   * @param {boolean} isLocked - Whether bundle products are present
   */
  function setBundleDiscountDisclosureState(isLocked) {
    const wasLocked = isBundleDiscountDisclosureLocked;
    isBundleDiscountDisclosureLocked = isLocked;

    const discountInputWrapper = document.querySelector('[data-rebuy-component="discount-input"]');
    if (!discountInputWrapper) {
      log('Discount input wrapper not found yet');
      return;
    }

    const disclosure = discountInputWrapper.querySelector('.rebuy-cart__discount-disclosure');
    if (!disclosure) {
      log('Bundle discount disclosure not found yet');
      return;
    }

    const summary = disclosure.querySelector('.rebuy-cart__discount-summary');
    const label = disclosure.querySelector('.rebuy-cart__discount-summary-label');
    const arrow = disclosure.querySelector('.rebuy-cart__discount-summary-arrow');

    if (isLocked) {
      suspendURLDiscountDisclosureObserver(discountInputWrapper);

      if (label && !('bundleDiscountOriginalLabel' in disclosure.dataset)) {
        disclosure.dataset.bundleDiscountOriginalLabel = label.textContent.trim();
      }

      if (summary) {
        storeDisclosureAttribute(disclosure, summary, 'aria-disabled', 'bundleDiscountAriaDisabled');
        storeDisclosureAttribute(disclosure, summary, 'tabindex', 'bundleDiscountTabindex');
      }

      if (arrow && !('bundleDiscountArrowDisplayPresent' in disclosure.dataset)) {
        disclosure.dataset.bundleDiscountArrowDisplayPresent = String(
          Boolean(arrow.style.getPropertyValue('display'))
        );
        disclosure.dataset.bundleDiscountArrowDisplay = arrow.style.getPropertyValue('display');
        disclosure.dataset.bundleDiscountArrowDisplayPriority =
          arrow.style.getPropertyPriority('display');
      }

      disclosure.classList.add('bwb-cart__discount-disclosure--bundle-locked');
      disclosure.removeAttribute('open');

      if (label && label.textContent !== 'BUNDLE-RABATT ANGEWENDET!') {
        label.textContent = 'BUNDLE-RABATT ANGEWENDET!';
      }

      if (summary) {
        summary.setAttribute('aria-disabled', 'true');
        summary.tabIndex = -1;
      }

      if (
        arrow &&
        (arrow.style.getPropertyValue('display') !== 'none' ||
          arrow.style.getPropertyPriority('display') !== 'important')
      ) {
        arrow.style.setProperty('display', 'none', 'important');
      }

      attachBundleDisclosureObserver(discountInputWrapper);
      log('Locked bundle discount disclosure');
      return;
    }

    detachBundleDisclosureObserver(discountInputWrapper);
    disclosure.classList.remove('bwb-cart__discount-disclosure--bundle-locked');

    if (label && 'bundleDiscountOriginalLabel' in disclosure.dataset) {
      label.textContent = disclosure.dataset.bundleDiscountOriginalLabel;
      delete disclosure.dataset.bundleDiscountOriginalLabel;
    }

    if (summary) {
      restoreDisclosureAttribute(disclosure, summary, 'aria-disabled', 'bundleDiscountAriaDisabled');
      restoreDisclosureAttribute(disclosure, summary, 'tabindex', 'bundleDiscountTabindex');
    }

    if (arrow && 'bundleDiscountArrowDisplayPresent' in disclosure.dataset) {
      if (disclosure.dataset.bundleDiscountArrowDisplayPresent === 'true') {
        arrow.style.setProperty(
          'display',
          disclosure.dataset.bundleDiscountArrowDisplay,
          disclosure.dataset.bundleDiscountArrowDisplayPriority
        );
      } else {
        arrow.style.removeProperty('display');
      }
      delete disclosure.dataset.bundleDiscountArrowDisplayPresent;
      delete disclosure.dataset.bundleDiscountArrowDisplay;
      delete disclosure.dataset.bundleDiscountArrowDisplayPriority;
    }

    if (wasLocked && window.URLDiscountManager?.refresh) {
      window.URLDiscountManager.refresh();
    }
    log('Restored bundle discount disclosure');
  }

  /**
   * Main function to process cart and update UI accordingly
   * @param {Object} cart - Rebuy cart object
   */
  function processCart(cart) {
    // Focus on DOM-based detection which works reliably
    const bundleItems = detectBundleItems();
    const hasBundle = bundleItems.length > 0;
    const bundleCount = bundleItems.length;

    // Prevent other discount codes being entered while the bundle discount applies.
    setBundleDiscountDisclosureState(hasBundle);

    // Check if bundle count has changed
    const bundleCountChanged = bundleCount !== activeBundleCount;

    // Calculate and apply bundle discount if bundles are present
    if (hasBundle) {
      const discountPercent = calculateBundleDiscount(bundleItems);

      // Update prices if anything has changed or to ensure consistency
      if (bundleCountChanged || discountPercent !== activeDiscountPercent) {
        log(
          `Bundle state changed: count=${bundleCount} (was ${activeBundleCount}), discount=${discountPercent}% (was ${activeDiscountPercent}%)`
        );
        updateItemPrices(bundleItems, discountPercent);
        activeDiscountPercent = discountPercent;
        activeBundleCount = bundleCount;
      } else {
        // Even if nothing changed, ensure prices are updated
        // This handles the case where removing an item doesn't change the tier
        log('Bundle state unchanged, ensuring prices are updated');
        updateItemPrices(bundleItems, discountPercent);
      }
    } else if (activeDiscountPercent > 0 || activeBundleCount > 0) {
      // Reset if no bundles but we previously had discounts
      log('No bundles detected, resetting prices');
      resetBundlePrices();
      activeDiscountPercent = 0;
      activeBundleCount = 0;
    }
  }

  /**
   * Wrapper for processCart that includes error handling
   * @param {Object} cart - Rebuy cart object
   */
  function safeProcessCart(cart) {
    try {
      processCart(cart);
    } catch (error) {
      console.error('[BundleBuilderHandler] Error processing cart:', error);
    }
  }

  /**
   * Sets up event listeners using RebuyEventManager
   * @returns {boolean} Success status
   */
  function setupEventListeners() {
    if (!window.RebuyEventManager) {
      console.error('[BundleBuilderHandler] ERROR: RebuyEventManager is not available!');
      return false;
    }

    // Register for all relevant cart events
    window.RebuyEventManager.on('cart:change', data => {
      log('cart:change event received');
      setTimeout(() => safeProcessCart(data.cart), 100);
    })
      .on('cart:ready', data => {
        log('cart:ready event received');
        setTimeout(() => safeProcessCart(data.cart), 100);
      })
      .on('cart:show', data => {
        log('cart:show event received');
        // Run immediately and once with delay to handle race conditions
        safeProcessCart(data.cart);
        setTimeout(() => safeProcessCart(data.cart), 300);
      });

    log('Event listeners registered successfully');
    return true;
  }

  /**
   * Clears all saved state and restores bundle-specific UI state
   */
  function clearState() {
    originalPrices.clear();
    activeDiscountPercent = 0;
    activeBundleCount = 0;
    log('Cleared all saved state');

    setBundleDiscountDisclosureState(false);
  }

  /**
   * Attempts initialization with retries
   * @returns {boolean} Success status
   */
  function attemptInitialization() {
    if (initialized) return true;

    initAttempts++;
    log(`Initialization attempt ${initAttempts}/${MAX_INIT_ATTEMPTS}`);

    // Check if Rebuy is ready
    if (!isRebuyReady()) {
      if (initAttempts < MAX_INIT_ATTEMPTS) {
        // Try again after a delay
        log('Rebuy not fully loaded yet, will retry initialization');
        setTimeout(attemptInitialization, 500);
        return false;
      } else {
        console.error(
          '[BundleBuilderHandler] Failed to initialize: Rebuy not available after maximum attempts'
        );
        return false;
      }
    }

    // Setup event listeners and mark as initialized
    if (setupEventListeners()) {
      initialized = true;
      log('Successfully initialized');

      // Initial processing if cart is already visible
      try {
        window.Rebuy.Cart.getCart().then(safeProcessCart);
      } catch (error) {
        log('Error getting initial cart, will rely on events');
      }

      return true;
    }

    return false;
  }

  // Public API
  return {
    /**
     * Initializes the handler
     * @returns {Object} this - For chainable calls
     */
    init: function () {
      if (initialized) {
        log('Already initialized');
        return this;
      }

      log('Starting initialization sequence');
      attemptInitialization();

      return this;
    },

    /**
     * Enables or disables debug logging
     * @param {boolean} enabled - Whether to enable debug logging
     * @returns {Object} this - For chainable calls
     */
    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },

    /**
     * Forces a refresh of the cart processing
     * @returns {Object} this - For chainable calls
     */
    refresh: function () {
      if (!initialized) {
        log('Cannot refresh, not initialized');
        return this;
      }

      try {
        if (window.Rebuy && window.Rebuy.Cart) {
          window.Rebuy.Cart.getCart().then(safeProcessCart);
        }
      } catch (error) {
        console.error('[BundleBuilderHandler] Error refreshing:', error);
      }

      return this;
    },

    /**
     * For testing: Forces an update of bundle items
     * @returns {boolean} Success status
     */
    forceUpdate: function () {
      log('Forcing update of all bundle items');
      const bundleItems = detectBundleItems();
      if (bundleItems.length) {
        const discountPercent = calculateBundleDiscount(bundleItems);
        updateItemPrices(bundleItems, discountPercent);
        return true;
      }
      return false;
    },

    /**
     * Resets all stored state
     * @returns {Object} this - For chainable calls
     */
    reset: function () {
      clearState();
      return this;
    },

    /**
     * For debugging: Returns all stored original prices
     * @returns {Object} Original prices as a plain object
     */
    getOriginalPrices: function () {
      return Object.fromEntries(originalPrices);
    },
  };
})();
